import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { useState } from 'react';
import {
  useGetCustomerQuery,
  useGetCustomerHistoryQuery,
  useRecordCustomerPaymentMutation,
  useDeleteCustomerPaymentMutation,
  useDeleteSalesReturnMutation,
  useAllocateCustomerAdvanceMutation,
} from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';
import { getLocalDateString } from '../../../shared/utils/dateUtils';

function CustomerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const { data: customer, isLoading: loadingCustomer, error: customerError } = useGetCustomerQuery(id);
  const { data: history, isLoading: loadingHistory, error: historyError } = useGetCustomerHistoryQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const [recordCustomerPayment, { isLoading: savingPayment }] = useRecordCustomerPaymentMutation();
  const [deleteCustomerPayment, { isLoading: deletingPayment }] = useDeleteCustomerPaymentMutation();
  const [deleteSalesReturn, { isLoading: deletingReturn }] = useDeleteSalesReturnMutation();
  const [allocateAdvance, { isLoading: allocatingAdvance }] = useAllocateCustomerAdvanceMutation();
  const currency = company?.currency || 'USD';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
        maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}`;
    }
  };

  const salesOrders = history?.salesOrders || [];
  const salesReturns = history?.salesReturns || [];
  const customerPayments = history?.customerPayments || history?.paymentTransactions || [];

  const openingBalance = Number(customer?.openingBalance || 0);
  const totalInvoicedAmount = salesOrders.reduce(
    (sum, so) => sum + Number(so.totalAmount || 0),
    0
  );
  const totalReturnsAmount = salesReturns.reduce(
    (sum, sr) => sum + Number(sr.totalAmount || 0),
    0
  );
  const totalPaymentsAmount = customerPayments
    .filter((p) => !p.type?.toLowerCase().includes('advance applied'))
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const totalDebits = openingBalance + totalInvoicedAmount;
  const totalCredits = totalPaymentsAmount + totalReturnsAmount;
  const openBalance = history?.summary?.netOutstanding != null
    ? Number(history.summary.netOutstanding)
    : Math.max(0, totalDebits - totalCredits);
  const advanceBalance = history?.summary?.availableAdvance != null
    ? Number(history.summary.availableAdvance)
    : Math.max(0, totalCredits - totalDebits);

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveForm, setReceiveForm] = useState({
    paymentDate: getLocalDateString(),
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });

  const [allocateOpen, setAllocateOpen] = useState(false);
  const [allocateAmount, setAllocateAmount] = useState('');

  const handleDeletePayment = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment record? This will reverse the journal entries and update outstanding/advance balances.')) {
      try {
        await deleteCustomerPayment({ customerId: id, paymentId }).unwrap();
      } catch (err) {
        alert(err?.data?.message || err?.message || 'Error deleting payment');
      }
    }
  };

  const handleDeleteReturn = async (returnId) => {
    if (window.confirm('Are you sure you want to delete this sales return? This will return the stock out of inventory and reverse the journal entries.')) {
      try {
        await deleteSalesReturn(returnId).unwrap();
      } catch (err) {
        alert(err?.data?.message || err?.message || 'Error deleting sales return');
      }
    }
  };

  if (loadingCustomer || loadingHistory) {
    return <Typography sx={{ p: 3 }}>Loading customer details...</Typography>;
  }
  if (customerError) {
    return <Typography color="error" sx={{ p: 3 }}>Error loading customer details.</Typography>;
  }
  if (historyError) {
    return <Typography color="error" sx={{ p: 3 }}>Error loading customer sales history.</Typography>;
  }

  return (
    <Box sx={{ pb: 6 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/customers')} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {customer?.name || 'Customer'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {customer?.code && `Code: ${customer.code}  · `}
              {customer?.phone ? `Phone: ${customer.phone}  · ` : ''}
              {customer?.email || ''}
            </Typography>
            {customer?.locationUrl && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Location:{' '}
                <a href={customer.locationUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: 500 }}>
                  Open map
                </a>
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<SummarizeOutlinedIcon />}
            onClick={() => navigate(`/reports/customer-ledger?customerId=${id}`)}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Statement of Account
          </Button>
          {advanceBalance > 0 && openBalance > 0 && (
            <Button
              variant="contained"
              color="success"
              sx={{ borderRadius: 2 }}
              onClick={() => {
                setAllocateAmount(Math.min(advanceBalance, openBalance).toFixed(typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2));
                setAllocateOpen(true);
              }}
            >
              Apply Advance to Balance
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddShoppingCartIcon />}
            onClick={() => navigate(`/sales/orders/new?customerId=${id}`)}
            sx={{ borderRadius: 2 }}
          >
            Create Order
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AssignmentReturnIcon />}
            onClick={() => navigate(`/sales/returns/new?customerId=${id}`)}
            sx={{ borderRadius: 2 }}
          >
            Create Sales Return
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setReceiveForm((prev) => ({
                ...prev,
                paymentDate: getLocalDateString(),
                amount: openBalance > 0 ? openBalance.toFixed(typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2) : '',
                paymentMethod: 'cash',
                paymentAccountId: '',
                referenceNo: '',
                notes: '',
              }));
              setReceiveOpen(true);
            }}
            sx={{ borderRadius: 2 }}
          >
            {openBalance > 0 ? 'Receive Payment' : 'Receive Advance'}
          </Button>
        </Box>
      </Box>

      {/* KPI Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0', minWidth: 190, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Outstanding Receivables
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: openBalance > 0 ? '#ea580c' : '#15803d', mt: 0.5 }}>
            {formatAmount(openBalance)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Net Ledger Balance
          </Typography>
        </Box>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', minWidth: 190, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#166534', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Total Payments Received
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#15803d', mt: 0.5 }}>
            {formatAmount(totalPaymentsAmount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {customerPayments.length} transaction{customerPayments.length === 1 ? '' : 's'}
          </Typography>
        </Box>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca', minWidth: 190, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Total Sales Returns
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#b91c1c', mt: 0.5 }}>
            {formatAmount(totalReturnsAmount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {salesReturns.length} return{salesReturns.length === 1 ? '' : 's'} recorded
          </Typography>
        </Box>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 190, flex: 1 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Total Invoiced Sales
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>
            {formatAmount(totalInvoicedAmount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {salesOrders.length} order{salesOrders.length === 1 ? '' : 's'}
          </Typography>
        </Box>

        {openingBalance > 0 && (
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0', minWidth: 190, flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Opening Balance
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#475569', mt: 0.5 }}>
              {formatAmount(openingBalance)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Initial starting balance
            </Typography>
          </Box>
        )}

        {advanceBalance > 0 && (
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', minWidth: 190, flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Available Advance
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1d4ed8', mt: 0.5 }}>
              {formatAmount(advanceBalance)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Unapplied credit balance
            </Typography>
          </Box>
        )}
      </Box>

      {/* Navigation Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={`Sales Orders (${salesOrders.length})`} sx={{ fontWeight: 600 }} />
        <Tab label={`Sales Returns (${salesReturns.length})`} sx={{ fontWeight: 600 }} />
        <Tab label={`Payment Transactions (${customerPayments.length})`} sx={{ fontWeight: 600 }} />
      </Tabs>

      {/* Tab 0: Sales Orders */}
      {tab === 0 && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                Sales Orders
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Order Number</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Invoice Number</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Invoice Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Sales Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>{`Total (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>{`Paid (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>{`Balance (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesOrders.length > 0 ? (
                    salesOrders.map((so) => (
                      <TableRow key={so.id} hover>
                        <TableCell sx={{ fontWeight: 600, color: '#2563eb' }}>{so.orderNumber}</TableCell>
                        <TableCell>{so.invoiceNumber || '-'}</TableCell>
                        <TableCell>
                          {so.invoiceDate ? new Date(so.invoiceDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>
                          <Chip
                            label={so.salesType || 'credit'}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              bgcolor: so.salesType === 'credit' ? '#fef3c7' : '#e0f2fe',
                              color: so.salesType === 'credit' ? '#92400e' : '#0369a1',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatAmount(so.totalAmount)}</TableCell>
                        <TableCell sx={{ fontWeight: 500, color: 'success.main' }}>{formatAmount(so.paidAmount)}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: Number(so.balanceAmount) > 0 ? '#ea580c' : 'text.secondary' }}>
                          {formatAmount(so.balanceAmount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={so.status || 'completed'}
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: 'capitalize', fontSize: '0.75rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/sales/orders/${so.id}`)}
                            title="View Sales Order"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No sales orders found for this customer.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 1: Sales Returns */}
      {tab === 1 && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                Sales Returns & Credit Notes
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<AssignmentReturnIcon />}
                onClick={() => navigate(`/sales/returns/new?customerId=${id}`)}
                sx={{ borderRadius: 1.5 }}
              >
                New Return
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Return Number</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Return Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Linked Order / Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Settlement Method</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>{`Total Return (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesReturns.length > 0 ? (
                    salesReturns.map((sr) => {
                      const method = sr.refundMethod || 'credit';
                      return (
                        <TableRow key={sr.id} hover>
                          <TableCell sx={{ fontWeight: 600, color: '#b91c1c' }}>{sr.returnNumber}</TableCell>
                          <TableCell>
                            {sr.returnDate ? new Date(sr.returnDate).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            {sr.salesOrder ? (
                              <Typography
                                variant="body2"
                                sx={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
                                onClick={() => navigate(`/sales/orders/${sr.salesOrder.id}`)}
                              >
                                {sr.salesOrder.orderNumber}
                                {sr.salesOrder.invoiceNumber ? ` (${sr.salesOrder.invoiceNumber})` : ''}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">General Return</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {method === 'credit' && (
                              <Chip
                                label="On-Account (Credit Note)"
                                size="small"
                                sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '0.75rem' }}
                              />
                            )}
                            {method === 'cash' && (
                              <Chip
                                label="Cash Refund"
                                size="small"
                                sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: '0.75rem' }}
                              />
                            )}
                            {method === 'bank' && (
                              <Chip
                                label="Bank Deposit"
                                size="small"
                                sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 600, fontSize: '0.75rem' }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#b91c1c' }}>
                            {formatAmount(sr.totalAmount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sr.status || 'posted'}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize', fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/sales/returns/${sr.id}`)}
                              title="View Details"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={deletingReturn}
                              onClick={() => handleDeleteReturn(sr.id)}
                              title="Delete Return & Reverse Stock/Ledgers"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No sales returns recorded for this customer.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Payment Transactions History */}
      {tab === 2 && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                Payment Transactions & Advances
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setReceiveForm((prev) => ({
                    ...prev,
                    paymentDate: getLocalDateString(),
                    amount: openBalance > 0 ? openBalance.toFixed(typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2) : '',
                    paymentMethod: 'cash',
                    paymentAccountId: '',
                    referenceNo: '',
                    notes: '',
                  }));
                  setReceiveOpen(true);
                }}
                sx={{ borderRadius: 1.5 }}
              >
                Receive Payment
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Payment Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Transaction Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Linked Order / Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>{`Amount (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Notes / Particulars</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5, textAlign: 'center' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerPayments.length > 0 ? (
                    customerPayments.map((p) => {
                      const method = (p.paymentMethod || 'cash').toLowerCase();
                      const isAdvance = p.type?.toLowerCase().includes('advance') || method === 'advance';
                      return (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={p.type || 'Customer Payment'}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                bgcolor: isAdvance ? '#dcfce7' : '#e0e7ff',
                                color: isAdvance ? '#15803d' : '#3730a3',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {p.salesOrder ? (
                              <Typography
                                variant="body2"
                                sx={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
                                onClick={() => navigate(`/sales/orders/${p.salesOrder.id}`)}
                              >
                                {p.salesOrder.orderNumber}
                                {p.salesOrder.invoiceNumber ? ` (${p.salesOrder.invoiceNumber})` : ''}
                              </Typography>
                            ) : p.orderNumber ? (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {p.orderNumber}
                                {p.invoiceNumber ? ` (${p.invoiceNumber})` : ''}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">On-Account / Direct</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={method.toUpperCase()}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'capitalize',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#15803d' }}>
                            {formatAmount(p.amount)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.notes || p.referenceNo || p.accountName || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={deletingPayment}
                              onClick={() => handleDeletePayment(p.id)}
                              title="Delete Payment & Reverse Ledgers"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No payment transaction history recorded for this customer.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Receive Payment Modal */}
      <Dialog
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          Receive Payment from Customer
        </DialogTitle>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <TextField
            type="date"
            label="Payment Date *"
            value={receiveForm.paymentDate}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, paymentDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Payment Method *"
            value={receiveForm.paymentMethod}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
            fullWidth
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="bank">Bank Transfer / Deposit</MenuItem>
            <MenuItem value="card">Credit / Debit Card</MenuItem>
            <MenuItem value="cheque">Cheque</MenuItem>
          </TextField>
          <TextField
            select
            label="Deposit Account (optional)"
            value={receiveForm.paymentAccountId}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, paymentAccountId: e.target.value }))}
            fullWidth
          >
            <MenuItem value="">Auto (Default by method)</MenuItem>
            {paymentAccounts?.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={`Amount (${currency}) *`}
            value={receiveForm.amount}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, amount: e.target.value }))}
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            helperText={openBalance > 0 ? `Current outstanding balance: ${formatAmount(openBalance)}` : 'Excess amount will be saved as customer advance balance.'}
          />
          <TextField
            label="Reference Number / Cheque No"
            value={receiveForm.referenceNo}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, referenceNo: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Notes / Description"
            value={receiveForm.notes}
            onChange={(e) => setReceiveForm((prev) => ({ ...prev, notes: e.target.value }))}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setReceiveOpen(false)} disabled={savingPayment}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={savingPayment}
            onClick={async () => {
              const amt = Number(receiveForm.amount || 0);
              if (!amt || amt <= 0) {
                alert('Amount must be greater than zero');
                return;
              }
              try {
                await recordCustomerPayment({
                  id,
                  paymentDate: receiveForm.paymentDate,
                  amount: amt,
                  paymentMethod: receiveForm.paymentMethod,
                  paymentAccountId: receiveForm.paymentAccountId || undefined,
                  referenceNo: receiveForm.referenceNo || undefined,
                  notes: receiveForm.notes || undefined,
                }).unwrap();
                setReceiveOpen(false);
              } catch (err) {
                alert(err?.data?.message || 'Error recording customer payment');
              }
            }}
          >
            {savingPayment ? 'Saving...' : 'Save Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Advance Modal */}
      <Dialog
        open={allocateOpen}
        onClose={() => setAllocateOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          Apply Advance to Outstanding Balance
        </DialogTitle>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
            <Typography variant="body2" color="text.secondary">
              Available Advance: <strong>{formatAmount(advanceBalance)}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Outstanding Balance: <strong>{formatAmount(openBalance)}</strong>
            </Typography>
          </Box>
          <TextField
            type="number"
            label={`Amount to Apply (${currency}) *`}
            value={allocateAmount}
            onChange={(e) => setAllocateAmount(e.target.value)}
            fullWidth
            inputProps={{ min: 0.01, max: Math.min(advanceBalance, openBalance), step: 0.01 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setAllocateOpen(false)} disabled={allocatingAdvance}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={allocatingAdvance}
            onClick={async () => {
              const amt = Number(allocateAmount || 0);
              if (!amt || amt <= 0) {
                alert('Amount must be greater than zero');
                return;
              }
              if (amt > advanceBalance) {
                alert(`Amount cannot exceed available advance balance (${formatAmount(advanceBalance)})`);
                return;
              }
              try {
                await allocateAdvance({
                  id,
                  amount: amt,
                }).unwrap();
                setAllocateOpen(false);
              } catch (err) {
                alert(err?.data?.message || 'Error allocating advance');
              }
            }}
          >
            {allocatingAdvance ? 'Applying...' : 'Apply Advance'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomerDetails;
